export const COOKIE_CONSENT_NAME = "dibs_cookie_consent";
export const COOKIE_CONSENT_STORAGE_KEY = "dibs_cookie_consent";
export const COOKIE_CONSENT_MAX_AGE = 60 * 60 * 24 * 365; // 365 days
export const COOKIE_CONSENT_VERSION = 1;
export const SIDEBAR_COOKIE_NAME = "sidebar:state";
export const COOKIE_CONSENT_UPDATED_EVENT = "dibs:cookie-consent-updated";
export const OPEN_COOKIE_PREFERENCES_EVENT = "dibs:open-cookie-preferences";

export type CookieCategory = "essential" | "functional" | "analytics";

export interface CookieConsentPreferences {
  version: number;
  essential: true;
  functional: boolean;
  analytics: boolean;
  updatedAt: string;
}

export interface CookieConsentInput {
  functional: boolean;
  analytics: boolean;
}

type LocationLike = Pick<Location, "hostname" | "protocol">;

const isLoopbackOrIpHost = (hostname: string): boolean => {
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname === "[::1]"
  ) {
    return true;
  }
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
};

/**
 * Resolves the root domain attribute (`.usedibs.com`) when running on a
 * production or staging `usedibs.com` host so consent is shared across
 * `usedibs.com` and `admin.usedibs.com`. Returns `null` on localhost, IP
 * hosts, or preview domains (`.vercel.app`, `.netlify.app`) where host-only
 * cookies should be used.
 */
export const resolveCookieDomain = (hostname: string): string | null => {
  const normalized = hostname.trim().toLowerCase();
  if (!normalized || isLoopbackOrIpHost(normalized)) {
    return null;
  }
  if (
    normalized.endsWith(".vercel.app") ||
    normalized.endsWith(".netlify.app")
  ) {
    return null;
  }
  if (normalized === "usedibs.com" || normalized.endsWith(".usedibs.com")) {
    return ".usedibs.com";
  }
  return null;
};

export const parseConsentValue = (
  raw: string | null | undefined,
): CookieConsentPreferences | null => {
  if (!raw) return null;
  const candidates = [raw];
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded !== raw) {
      candidates.unshift(decoded);
    }
  } catch {
    // Ignore malformed URI sequences and fall back to raw JSON parse
  }

  for (const candidate of candidates) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (!parsed || typeof parsed !== "object") continue;
      const record = parsed as Record<string, unknown>;
      if (record.version !== COOKIE_CONSENT_VERSION) continue;
      if (typeof record.functional !== "boolean") continue;
      if (typeof record.analytics !== "boolean") continue;
      const updatedAt =
        typeof record.updatedAt === "string" && record.updatedAt.trim().length > 0
          ? record.updatedAt
          : new Date(0).toISOString();

      return {
        version: COOKIE_CONSENT_VERSION,
        essential: true,
        functional: record.functional,
        analytics: record.analytics,
        updatedAt,
      };
    } catch {
      continue;
    }
  }

  return null;
};

export const readCookieValue = (
  cookieHeader: string,
  name: string,
): string | null => {
  if (!cookieHeader) return null;
  const parts = cookieHeader.split(";");
  for (const part of parts) {
    const eqIdx = part.indexOf("=");
    if (eqIdx === -1) continue;
    const key = part.slice(0, eqIdx).trim();
    if (key === name) {
      return part.slice(eqIdx + 1).trim();
    }
  }
  return null;
};

export const serializeConsentCookie = (
  prefs: CookieConsentPreferences,
  location?: LocationLike,
): string => {
  const encoded = encodeURIComponent(JSON.stringify(prefs));
  const segments = [
    `${COOKIE_CONSENT_NAME}=${encoded}`,
    "path=/",
    `max-age=${COOKIE_CONSENT_MAX_AGE}`,
    "SameSite=Lax",
  ];

  const domain = location ? resolveCookieDomain(location.hostname) : null;
  if (domain) {
    segments.push(`domain=${domain}`);
  }
  if (location?.protocol === "https:") {
    segments.push("Secure");
  }

  return segments.join("; ");
};

export const expireCookie = (
  name: string,
  location?: LocationLike,
): void => {
  if (typeof document === "undefined") return;
  const loc =
    location ??
    (typeof window !== "undefined" ? window.location : undefined);
  const securePart = loc?.protocol === "https:" ? "; Secure" : "";

  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${securePart}`;

  const domain = loc ? resolveCookieDomain(loc.hostname) : null;
  if (domain) {
    document.cookie = `${name}=; path=/; max-age=0; domain=${domain}; SameSite=Lax${securePart}`;
  }
};

export const readConsentPreferences = (): CookieConsentPreferences | null => {
  if (typeof document !== "undefined") {
    const rawCookie = readCookieValue(document.cookie, COOKIE_CONSENT_NAME);
    const parsedCookie = parseConsentValue(rawCookie);
    if (parsedCookie) {
      return parsedCookie;
    }
  }

  if (typeof window !== "undefined") {
    try {
      const rawStorage = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
      const parsedStorage = parseConsentValue(rawStorage);
      if (parsedStorage) {
        return parsedStorage;
      }
    } catch {
      // Ignore storage access errors in restricted browsing modes
    }
  }

  return null;
};

export const hasCookieConsent = (category: CookieCategory): boolean => {
  if (category === "essential") return true;
  const prefs = readConsentPreferences();
  if (!prefs) return false;
  return Boolean(prefs[category]);
};

export const saveConsentPreferences = (
  input: CookieConsentInput,
): CookieConsentPreferences => {
  const prefs: CookieConsentPreferences = {
    version: COOKIE_CONSENT_VERSION,
    essential: true,
    functional: Boolean(input.functional),
    analytics: Boolean(input.analytics),
    updatedAt: new Date().toISOString(),
  };

  if (typeof document !== "undefined") {
    const loc = typeof window !== "undefined" ? window.location : undefined;
    document.cookie = serializeConsentCookie(prefs, loc);
  }

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        COOKIE_CONSENT_STORAGE_KEY,
        JSON.stringify(prefs),
      );
    } catch {
      // Ignore localStorage quota/privacy errors
    }
  }

  if (!prefs.functional) {
    expireCookie(SIDEBAR_COOKIE_NAME);
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<CookieConsentPreferences>(COOKIE_CONSENT_UPDATED_EVENT, {
        detail: prefs,
      }),
    );
  }

  return prefs;
};

export const onCookieConsentChange = (
  listener: (prefs: CookieConsentPreferences) => void,
): (() => void) => {
  if (typeof window === "undefined") {
    return () => {};
  }
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<CookieConsentPreferences>;
    if (customEvent.detail) {
      listener(customEvent.detail);
    }
  };
  window.addEventListener(COOKIE_CONSENT_UPDATED_EVENT, handler);
  return () => window.removeEventListener(COOKIE_CONSENT_UPDATED_EVENT, handler);
};

export const openCookiePreferencesModal = (): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_PREFERENCES_EVENT));
};

const PUBLIC_MARKETING_EXACT_PATHS = new Set([
  "/",
  "/privacy-policy",
  "/terms-of-service",
  "/data-deletion",
  "/blog",
  "/auth",
  "/auth/verify-success",
  "/auth/reset-password",
  "/admin",
  "/staff-invite",
]);

/**
 * Determines whether the cookie consent banner should automatically appear on
 * the given route when the visitor has not made a choice yet.
 *
 * Shown on:
 * - Public marketing, blog, legal, and auth redirect routes
 * - Business admin & auth routes (`admin.` subdomain)
 *
 * Excluded on:
 * - Merchant booking & checkout pages (`/book/:id`, `/:slug`, `/listing/:id`,
 *   `/modify-booking/*`, `/booking-confirmed*`, `/cancel-booking*`, `/waitlist/*`, `/makeup/*`)
 * - Private customer capability pages (`/feedback`, `/recover`)
 * - Internal operator tools (`internal.` subdomain)
 */
export const shouldShowCookieBannerOnRoute = ({
  pathname,
  hostname,
  search = "",
  isDev = false,
}: {
  pathname: string;
  hostname: string;
  search?: string;
  isDev?: boolean;
}): boolean => {
  const normalizedHost = hostname.trim().toLowerCase();
  const normalizedPath =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname || "/";

  const devSubdomain = isDev
    ? new URLSearchParams(search).get("subdomain")
    : null;

  const isInternal =
    normalizedHost.startsWith("internal.") || devSubdomain === "internal";
  if (isInternal) {
    return false;
  }

  if (normalizedPath.startsWith("/dev/")) {
    return false;
  }

  if (normalizedPath === "/feedback" || normalizedPath === "/recover") {
    return false;
  }

  const isAdmin =
    normalizedHost.startsWith("admin.") || devSubdomain === "admin";
  if (isAdmin) {
    return true;
  }

  if (PUBLIC_MARKETING_EXACT_PATHS.has(normalizedPath)) {
    return true;
  }

  if (normalizedPath.startsWith("/blog/")) {
    return true;
  }

  return false;
};
